import { AuthBaseClient } from '../base-client';
import type {
  PatientResponse,
  CreatePatientRequest,
  CreatePatientInEhrRequest,
  UpdatePatientRequest,
  PatientLookupRequest,
  PatientFlagStatus,
} from './types';
import type { SearchRequest, SearchResponse } from '@vagusx/shared';

/**
 * Client for patient API endpoints.
 */
export class PatientClient extends AuthBaseClient {
  /**
   * Get patient by ID.
   */
  async getById(id: string): Promise<PatientResponse> {
    return this.get<PatientResponse>(`/api/patients/${id}`);
  }

  /**
   * Lookup patient by phone, MRN, or name+DOB.
   * Returns null if no patient found.
   */
  async lookup(criteria: PatientLookupRequest): Promise<PatientResponse | null> {
    return this.post<PatientResponse | null>('/api/patients/lookup', criteria);
  }

  /**
   * Search patients with pagination.
   */
  async search(request: SearchRequest): Promise<SearchResponse<PatientResponse>> {
    return this.post<SearchResponse<PatientResponse>>('/api/patients/search', request);
  }

  /**
   * Create a new patient.
   */
  async create(data: CreatePatientRequest): Promise<PatientResponse> {
    return this.post<PatientResponse>('/api/patients', data);
  }

  /**
   * Create a patient in the org's EHR (system of record) and link the local row
   * (ADR-089). Throws an operation-not-allowed validation error when the org's EHR
   * has no patient-write path (e.g. Office Ally) — callers may fall back to
   * {@link create} for a local-only patient.
   */
  async createInEhr(data: CreatePatientInEhrRequest): Promise<PatientResponse> {
    return this.post<PatientResponse>('/api/patients/ehr', data);
  }

  /**
   * Update a patient.
   */
  async update(id: string, data: UpdatePatientRequest): Promise<PatientResponse> {
    return this.patch<PatientResponse>(`/api/patients/${id}`, data);
  }

  /**
   * Delete a patient (soft delete).
   */
  async deletePatient(id: string): Promise<void> {
    return this.delete<void>(`/api/patients/${id}`);
  }

  /**
   * Flag a patient.
   */
  async flagPatient(id: string): Promise<PatientResponse> {
    return this.post<PatientResponse>(`/api/patients/${id}/flag`, {});
  }

  /**
   * Unflag a patient.
   */
  async unflagPatient(id: string): Promise<PatientResponse> {
    return this.post<PatientResponse>(`/api/patients/${id}/unflag`, {});
  }

  /**
   * Get flag status for a patient.
   */
  async getFlagStatus(id: string): Promise<PatientFlagStatus> {
    return this.get<PatientFlagStatus>(`/api/patients/${id}/flag-status`);
  }
}
